import { useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api } from '../../api/client';
import { KPICard } from '../../components/intelligence/KPICard';
import { FilterBar, createFilterState, filtersToParams } from '../../components/intelligence/FilterBar';
import { SkeletonKPI, SkeletonTable } from '../../components/intelligence/SkeletonLoader';
import { DataTable } from '../../components/intelligence/DataTable';

const TOOLS = [
 { key: 'chat', name: 'Chat', icon: '' },
 { key: 'tasks', name: 'Tarefas', icon: '' },
 { key: 'machines', name: 'Máquinas', icon: '' },
 { key: 'cloud', name: 'Cloud', icon: '' },
 { key: 'router', name: 'Router', icon: '' },
 { key: 'crm', name: 'CRM', icon: '' },
 { key: 'whatsapp', name: 'WhatsApp', icon: '' },
 { key: 'backlinks', name: 'Backlinks', icon: '' },
 { key: 'files', name: 'Arquivos', icon: '' },
 { key: 'wallet', name: 'Carteira', icon: '' },
 { key: 'plans', name: 'Planos', icon: '' },
 { key: 'devmode', name: 'Dev Mode', icon: '' },
];

interface ToolStat {
  tool: string;
  requests: number;
  unique_users: number;
  avg_latency: number;
  error_rate: number;
}

const loading = signal(true);
const error = signal('');
const stats = signal<ToolStat[]>([]);
const filters = createFilterState();

async function loadData() {
  loading.value = true;
  error.value = '';
  try {
    const params = filtersToParams(filters.value);
    params.set('event_category', 'tool_usage');
    const res = await api.get<any>(`/intelligence/aggregates?${params.toString()}`);
    const agg = res.aggregates || res;
    // Map from top_tools or generate empty
    stats.value = TOOLS.map((t) => {
      const found = (agg.top_tools || []).find((tt: any) => tt.tool === t.key);
      return {
        tool: t.key,
        requests: found?.count || 0,
        unique_users: found?.unique_users || 0,
        avg_latency: found?.avg_latency || 0,
        error_rate: found?.error_rate || 0,
      };
    });
  } catch (err: any) {
    error.value = err.message || 'Erro ao carregar uso';
    stats.value = TOOLS.map((t) => ({ tool: t.key, requests: 0, unique_users: 0, avg_latency: 0, error_rate: 0 }));
  } finally {
    loading.value = false;
  }
}

export function ToolUsagePanel() {
  useEffect(() => { loadData(); }, []);

  const totalRequests = stats.value.reduce((a, s) => a + s.requests, 0);
  const totalUsers = stats.value.reduce((a, s) => a + s.unique_users, 0);

  return (
    <div>
 <h2 class="text-xl font-bold text-white mb-4">Uso de Ferramentas</h2>
      <FilterBar filters={filters} onApply={loadData} showTool={false} />

      {error.value && (
        <div class="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-3 mb-4 text-sm">{error.value}</div>
      )}

      {/* Summary KPIs */}
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {loading.value ? (
          Array.from({ length: 4 }).map((_, i) => <SkeletonKPI key={i} />)
        ) : (
          <>
 <KPICard icon="" label="Total Requests" value={totalRequests} />
 <KPICard icon="" label="Usuários Únicos" value={totalUsers} />
 <KPICard icon="" label="Ferramentas Ativas" value={stats.value.filter((s) => s.requests > 0).length} />
 <KPICard icon="" label="Latência Média" value={`${Math.round(stats.value.reduce((a, s) => a + s.avg_latency, 0) / (stats.value.length || 1))}ms`} />
          </>
        )}
      </div>

      {/* Tool cards grid */}
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        {loading.value ? (
          Array.from({ length: 12 }).map((_, i) => <SkeletonKPI key={i} />)
        ) : (
          TOOLS.map((t) => {
            const s = stats.value.find((st) => st.tool === t.key);
            return (
              <div key={t.key} class="bg-gray-800 rounded-xl border border-gray-700 p-4 hover:bg-gray-750 transition-colors">
                <div class="flex items-center gap-2 mb-2">
                  <span class="text-xl">{t.icon}</span>
                  <span class="text-sm font-medium text-white">{t.name}</span>
                </div>
                <div class="text-2xl font-bold text-white mb-1">{(s?.requests || 0).toLocaleString()}</div>
                <div class="flex gap-3 text-xs text-gray-400">
 <span> {s?.unique_users || 0}</span>
 <span> {s?.avg_latency || 0}ms</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Detailed table */}
      {!loading.value && (
        <div class="[&>div]:!bg-gray-800 [&>div]:!border-gray-700 [&_th]:!bg-gray-800 [&_th]:!text-gray-400 [&_td]:!text-gray-300 [&_tr:hover]:!bg-gray-750">
          <DataTable
            columns={[
              { key: 'tool', label: 'Ferramenta', render: (r: ToolStat) => {
                const t = TOOLS.find((tt) => tt.key === r.tool);
                return <span>{t?.icon} {t?.name || r.tool}</span>;
              }},
              { key: 'requests', label: 'Requests', render: (r: ToolStat) => r.requests.toLocaleString() },
              { key: 'unique_users', label: 'Usuários', render: (r: ToolStat) => r.unique_users.toLocaleString() },
              { key: 'avg_latency', label: 'Latência Média', render: (r: ToolStat) => `${r.avg_latency}ms` },
              { key: 'error_rate', label: 'Taxa Erro', render: (r: ToolStat) => `${r.error_rate}%` },
            ]}
            data={stats.value}
            pageSize={12}
            emptyMessage="Sem dados de uso"
          />
        </div>
      )}
    </div>
  );
}
